import config from '../config';
import selectedEnvironment from '../env';
import authService from './auth.service';

const API_PCN_URL = `${config[selectedEnvironment].API_URL}/PCN`;

const DownloadPCNReport = async (CaseId, date, endDate, includeStornoChecks, auth) => {
    try {
        // Make the HTTP request to get the file data
        const response = await fetch(`${API_PCN_URL}/DownloadPCNReport`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId, Date: date, EndDate: endDate, IncludeStornoChecks: includeStornoChecks })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', 'PCN874.txt'); // Set the correct file name here
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    } catch (err) {
        console.error("Error downloading the report: ", err);
        alert("Error downloading the report. Please try again.");
    }
};

const DownloadPCNPDF = async (CaseId, date, endDate, includeStornoChecks) => {
    try {
        const auth = await authService.getCurrentToken();
        
        // Make the HTTP request to get the file data
        const response = await fetch(`${API_PCN_URL}/DownloadPCNReportPDF`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId, Date: date, EndDate: endDate, IncludeStornoChecks: includeStornoChecks })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', 'PCN874.pdf'); // Set the correct file name here
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    } catch (err) {
        console.error("Error downloading the report: ", err);
        alert("Error downloading the report. Please try again.");
    }
};

const CheckPCNDocuments = async (CaseId, date, endDate, includeStornoChecks = null) => {
    try {
        const auth = await authService.getCurrentToken();
        
        const response = await fetch(`${API_PCN_URL}/CheckPCNDocuments`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId, Date: date, EndDate: endDate, IncludeStornoChecks: includeStornoChecks })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (err) {
        console.error("Error checking PCN documents: ", err);
        throw err;
    }
};

/**
 * Fetches PCN report as JSON (same data as PCN874 file, structured).
 * @param {string} CaseId - Client case ID
 * @param {Date|string} date - Start date
 * @param {Date|string} endDate - End date
 * @param {boolean|null} includeStornoChecks - Include storno checks
 * @returns {Promise<object>} PCN874ReportDto
 */
const GetPCNReportAsJson = async (CaseId, date, endDate, includeStornoChecks = null) => {
    try {
        const auth = await authService.getCurrentToken();
        const response = await fetch(`${API_PCN_URL}/GetPCNReportAsJson`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                CaseId,
                Date: date,
                EndDate: endDate,
                IncludeStornoChecks: includeStornoChecks ?? undefined
            })
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data?.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    } catch (err) {
        console.error("Error fetching PCN report as JSON: ", err);
        throw err;
    }
};


/**
 * Sends VAT detailed report to the tax authority.
 * The backend builds VatReportReq server-side from the PCN data.
 * @param {object} payload - { caseId, action, date, endDate, includeStornoChecks, vatNumber?, reportType? }
 */
const SendVatDetailedReporting = async (payload) => {
    try {
        const auth = await authService.getCurrentToken();
        const body = {
            CaseId: payload.caseId,
            Action: payload.action,
            Date: payload.date,
            EndDate: payload.endDate,
            IncludeStornoChecks: payload.includeStornoChecks ?? null,
            VatNumber: payload.vatNumber ?? 0,
            ReportType: payload.reportType ?? 1,
        };
        const response = await fetch(`${API_PCN_URL}/SendVatDetailedReporting`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const err = new Error(data?.message || `HTTP error! status: ${response.status}`);
            err.error = data?.error;
            err.details = data;
            throw err;
        }
        return data;
    } catch (err) {
        console.error("Error sending VAT detailed report: ", err);
        throw err;
    }
};

const PCNService = {
    DownloadPCNReport,
    DownloadPCNPDF,
    CheckPCNDocuments,
    GetPCNReportAsJson,
    SendVatDetailedReporting,
};

export default PCNService; 